import { SHEET_HEADING_ACTION_CLASS } from "@/components/print/ExportButton";
import { Button } from "@/components/ui/button";
import { MenubarItem } from "@/components/ui/menubar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  type PinnableRecord,
  type RecordShortcutState,
  useRecordShortcut,
} from "@/hooks/use-record-shortcut";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { cn } from "@/lib/utils";
import { Pin } from "lucide-react";

/**
 * Commandes « Épingler à l'accueil ». Sur une fiche, la même commande est
 * proposée à deux endroits (menu Actions et à côté d'Exporter) : elles
 * partagent l'état de `useRecordShortcut`, appelé une seule fois par la fiche.
 */

function usePinLabel(pinned: boolean) {
  const vt = useVisibleT();
  return pinned
    ? vt("bo.home.pin.remove", "Retirer de l’accueil")
    : vt("bo.home.pin.add", "Épingler à l’accueil");
}

const PinIcon = ({
  pinned,
  className,
}: {
  pinned: boolean;
  className: string;
}) => (
  <Pin
    className={cn(className, pinned && "fill-primary text-primary")}
    aria-hidden
  />
);

/** Icône au-dessus du libellé, à côté d'« Exporter » (même gabarit). */
export const PinToHomeLabeledButton = ({
  pinned,
  toggle,
}: RecordShortcutState) => {
  const vt = useVisibleT();
  const label = usePinLabel(pinned);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="outline"
          onClick={toggle}
          aria-pressed={pinned}
          aria-label={label}
          className={SHEET_HEADING_ACTION_CLASS}
        >
          <PinIcon pinned={pinned} className="h-5 w-5" />
          {pinned
            ? vt("bo.home.pin.pinned", "Épinglée")
            : vt("bo.home.pin.short", "Épingler")}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom" variant="action" className="px-3">
        <span className="text-white text-sm font-medium">{label}</span>
      </TooltipContent>
    </Tooltip>
  );
};

/** Même bouton, pour les barres d'aperçu (œuvre, multiple) : gabarit d'Exporter. */
export const PinToHomeButton = (record: PinnableRecord) => {
  const state = useRecordShortcut(record);
  if (!state) return null;
  return <PinToHomeLabeledButton {...state} />;
};

/** Entrée du menu Actions de l'en-tête des fiches. */
export const PinToHomeMenuItem = ({ pinned, toggle }: RecordShortcutState) => {
  const label = usePinLabel(pinned);
  return (
    <MenubarItem onClick={toggle} className="gap-2">
      <PinIcon pinned={pinned} className="h-4 w-4 shrink-0" />
      {label}
    </MenubarItem>
  );
};
